import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  ArrowLeft,
  Mail,
  MapPin,
  Megaphone,
  Phone,
  Star,
  UserRound,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { paths } from "@shared-core/routing/routes";
import { campaignTicketLabel } from "@shared-core/domain/campaign-ticket-label";
import type * as API from "@shared-core/data/api-types";
import { CustomerImprovementLogs } from "../app/components/domain/customers/CustomerImprovementLogs";
import { NotesSection } from "../app/components/domain/customers/NotesSection";
import { Badge } from "../app/components/primitives/badge";
import {
  STATUS_CONFIG,
  type TicketStatusValue,
} from "../app/components/domain/tickets/TicketMetadataStrip";

/**
 * A contact's own drill-in page (`/contacts/:id`): the contact is a CIS
 * person at a customer, and `?customer_id=` picks the customer of a person at
 * several. Every ticket about this contact across both identity spaces
 * (rep-linked via customer_person_id, or campaign-raised via the linked
 * marketing person's person_id), the linked marketing person when this
 * contact is also known to marketing_contacts_module, and the customer's
 * improvement logs
 * (deliberately customer-level, not contact-level — labeled as such — see
 * MarketingPersonDetailPage for the reciprocal cross-link).
 */
export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get("customer_id") ?? undefined;
  const navigate = useNavigate();
  const dataAdapter = useDataAdapter();
  const [contact, setContact] = useState<API.ContactDetailResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // The rep-set key-decision-maker flag for this person at this customer,
  // read from the detail row and then toggled optimistically.
  const [isKeyPerson, setIsKeyPerson] = useState(false);
  const [keyPersonSaving, setKeyPersonSaving] = useState(false);
  const [customerAddress, setCustomerAddress] =
    useState<API.DeliveryAddress | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    dataAdapter
      .getContactDetail(id, customerParam)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setNotFound(true);
          return;
        }
        // An old link to a Tharstern contact number: the person it became.
        if (data.moved_to) {
          navigate(
            paths.contactDetail({
              id: data.moved_to,
              customerId: data.customer_id,
            }),
            { replace: true },
          );
        }
        setContact(data);
        setIsKeyPerson(data.is_key_decision_maker ?? false);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, id, customerParam, navigate]);

  // The customer's default delivery address, explicitly labeled as the
  // customer's (not the contact's) — Tharstern contacts carry no address
  // of their own, only the customer does, via a delivery-address book.
  useEffect(() => {
    const customerId = contact?.customer_id;
    if (!customerId) {
      setCustomerAddress(null);
      return;
    }
    let cancelled = false;
    dataAdapter
      .getDefaultDeliveryAddress(customerId)
      .then((address) => {
        if (!cancelled) setCustomerAddress(address);
      })
      .catch(() => {
        if (!cancelled) setCustomerAddress(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, contact?.customer_id]);

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (notFound || !contact) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <p className="text-sm text-red-600">
          This contact doesn't exist, or you don't have access to it.
        </p>
      </div>
    );
  }

  const tickets = contact.tickets ?? [];
  const campaigns = contact.campaigns ?? [];

  const handleToggleKeyPerson = async () => {
    const next = !isKeyPerson;
    setKeyPersonSaving(true);
    setIsKeyPerson(next);
    try {
      await dataAdapter.setContactKeyPerson(
        contact.id,
        contact.customer_id,
        next,
      );
    } catch {
      setIsKeyPerson(!next);
    } finally {
      setKeyPersonSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {contact.customer_id && (
        <Link
          to={paths.customerDetail({ customerId: contact.customer_id })}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to {contact.customer?.name ?? "customer"}
        </Link>
      )}

      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">{contact.name}</h1>
          <button
            type="button"
            onClick={handleToggleKeyPerson}
            disabled={keyPersonSaving}
            aria-pressed={isKeyPerson}
            aria-label={
              isKeyPerson
                ? `Unmark ${contact.name} as key contact`
                : `Mark ${contact.name} as key contact`
            }
            title={
              isKeyPerson
                ? "Key contact — click to unflag"
                : "Mark as key contact"
            }
            className="text-muted-foreground/60 hover:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded disabled:opacity-60"
          >
            <Star
              className={`size-4 ${isKeyPerson ? "fill-amber-400 text-amber-500" : ""}`}
            />
          </button>
          {contact.is_primary_customer_contact && (
            <Badge variant="outline">Primary</Badge>
          )}
        </div>
        <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
          {contact.customer?.name && <p>{contact.customer.name}</p>}
          {contact.email_address && (
            <p className="flex items-center gap-2">
              <Mail className="size-3.5" />
              <a
                href={`mailto:${contact.email_address}`}
                className="hover:text-primary transition-colors"
              >
                {contact.email_address}
              </a>
            </p>
          )}
          {contact.phone_number_1 && (
            <p className="flex items-center gap-2">
              <Phone className="size-3.5" />
              <a
                href={`tel:${contact.phone_number_1}`}
                className="hover:text-primary transition-colors"
              >
                {contact.phone_number_1}
              </a>
            </p>
          )}
          {customerAddress && (
            <p className="flex items-start gap-2">
              <MapPin className="size-3.5 mt-0.5 shrink-0" />
              <span>
                <span className="text-xs uppercase tracking-wide text-muted-foreground/70">
                  Customer address
                </span>
                <br />
                {[
                  customerAddress.address,
                  customerAddress.postcode,
                  customerAddress.country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </p>
          )}
        </div>

        {contact.marketing_person ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <UserRound className="size-3.5" />
            Also a marketing contact —{" "}
            <Link
              to={paths.marketingPersonDetail({
                personId: contact.marketing_person.id,
              })}
              className="text-primary hover:underline"
            >
              view marketing profile
            </Link>
          </p>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            Not enrolled in marketing.
          </p>
        )}
      </div>

      <section>
        <h2 className="text-lg font-medium mb-2">Tickets ({tickets.length})</h2>
        {tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No tickets raised for this contact yet.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border bg-background">
            {tickets.map((ticket) => {
              const campaignName = campaignTicketLabel(ticket);
              return (
                <li key={ticket.id}>
                  <Link
                    to={paths.ticketDetail({ id: ticket.id })}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-muted/50"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="truncate">{ticket.title}</span>
                      {campaignName && (
                        <Badge
                          variant="outline"
                          className="shrink-0 gap-1 border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300"
                          title={campaignName}
                        >
                          <Megaphone className="size-3 shrink-0" />
                          <span className="truncate max-w-[140px]">
                            {campaignName}
                          </span>
                        </Badge>
                      )}
                    </span>
                    <Badge
                      variant={
                        STATUS_CONFIG[ticket.status as TicketStatusValue]
                          ?.variant ?? "secondary"
                      }
                    >
                      {STATUS_CONFIG[ticket.status as TicketStatusValue]
                        ?.label ?? ticket.status}
                    </Badge>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {contact.marketing_person && (
        <section>
          <h2 className="text-lg font-medium mb-2">
            Campaigns ({campaigns.length})
          </h2>
          {campaigns.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No campaigns sent to this contact yet.
            </p>
          ) : (
            <div className="border border-border rounded-md divide-y divide-border">
              {campaigns.map((touch) => (
                <div
                  key={touch.id}
                  className="px-3 py-2 flex items-center justify-between text-sm"
                >
                  <span>{touch.campaign_name}</span>
                  <span className="text-muted-foreground">
                    {touch.channel} · {touch.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <NotesSection
        title="Notes"
        subjectType="contact"
        subjectId={String(contact.id)}
        subjectLabel={contact.name}
        customerId={contact.customer_id}
        jobPickerCustomerId={contact.customer_id}
      />

      <div>
        <p className="text-xs text-muted-foreground mb-2">
          Improvement logs are tracked at the customer level, not per contact —
          these cover the whole account.
        </p>
        <CustomerImprovementLogs customerId={contact.customer_id} />
      </div>
    </div>
  );
}
